import { useTheme } from '@emotion/react';
import { Formik } from 'formik';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Button, EntryList, Modal, Text, TextField } from '~/components/blocks';
import { MedixsPatientDetailDialogProfileFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogPatientMobilePhoneState } from '~/state/partials/patient_detail_dialog/atoms';

import { Fields } from './types';
import { useEditMobilePhone } from './use-edit-mobile-phone';
import { validationSchema } from './validation';

type Props = {
  patient: MedixsPatientDetailDialogProfileFragment;
};
export const EditMobilePhoneModal = ({ patient }: Props) => {
  const theme = useTheme();
  const [{ isOpen }, setPatientMobilePhoneState] = useAtom(
    patientDetailDialogPatientMobilePhoneState,
  );

  const handleClose = useCallback(() => {
    setPatientMobilePhoneState((currentState) => ({
      ...currentState,
      isOpen: false,
    }));
  }, [setPatientMobilePhoneState]);

  const { isUpdating, updateMobilePhone } = useEditMobilePhone({
    patientId: patient.id,
    onCompleted: handleClose,
  });

  const handleSubmit = useCallback(
    async (values: Fields) => {
      await updateMobilePhone(values.mobilePhone);
    },
    [updateMobilePhone],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Modal size="s" open={isOpen} onClose={handleClose}>
      <Formik
        initialValues={{ mobilePhone: patient?.mobilePhone ?? '' }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, errors, handleChange, submitForm }) => {
          return (
            <>
              <Modal.Header>携帯電話番号編集</Modal.Header>
              <Modal.Body>
                <Text marginBottom={theme.space.l}>
                  患者が正しくSMSを受け取れるよう、最新の携帯電話番号を入力してください
                </Text>
                <EntryList>
                  <EntryList.Head>携帯電話番号</EntryList.Head>
                  <EntryList.Body>
                    <TextField
                      inputMode="numeric"
                      name="mobilePhone"
                      placeholder="08012345678"
                      value={values.mobilePhone}
                      error={errors.mobilePhone}
                      onChange={handleChange}
                    />
                  </EntryList.Body>
                </EntryList>
              </Modal.Body>
              <Modal.Footer>
                <Button use="base" size="m" type="button" loading={isUpdating} onClick={submitForm}>
                  保存
                </Button>
              </Modal.Footer>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};
